import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { sortBy } from "lodash"
import type { StateObject } from "../../redux/types"
import { getAccesses } from "../../data/selectors"
import type { AccessData, PermissionData } from "../../data/types"
import { getCurrentPathQuery, getRecordEditId } from "../../router/selectors"
import type { RoleFormItem } from "./RoleForm"
import type { RoleTableDataItem } from "./RolesTable"

export const getEditRoleId = (state: StateObject) => getRecordEditId(state)

export const getEditRole = (state: StateObject): Maybe<AccessData> => {
    return getAccesses(state).find(a => a.id === getEditRoleId(state))
}

export const PermissionType = {
    ClockInOut: "ACCESS_CLOCK_IN_OUT",
    RequireClockInToSignIn: "ACCESS_REQUIRE_CLOCK_IN_TO_SIGN_IN",
    BreaksEnabled: "ACCESS_TIMERECORD_BREAKS",
    UnpaidBreaksEnabled: "ACCESS_TIMERECORD_BREAKS_UNPAID",
    PaidBreaksEnabled: "ACCESS_TIMERECORD_BREAKS_PAID",
    LimitToDsrAccess: "ACCESS_LIMIT_ONLY_TO_DSR_REPORT",
    Register: "ACCESS_REGISTER",
    Inventory: "ACCESS_INVENTORY",
    Operations: "ACCESS_OPERATIONS",
    OperationsNoSale: "ACCESS_OPERATIONS_NO_SALE",
    OperationsTipAdjustment: "ACCESS_OPERATIONS_TIP_ADJUSTMENT",
    OperationsDeliveryManagement: "ACCESS_OPERATIONS_DELIVERY_MANAGEMENT",
    OperationsPendingPayments: "ACCESS_OPERATIONS_PENDING_PAYMENTS",
    OperationsShiftReview: "ACCESS_OPERATIONS_SHIFT_REVIEW",
    OperationsInventory: "ACCESS_OPERATIONS_INVENTORY",
    Settings: "ACCESS_SETTINGS",
    Reports: "ACCESS_REPORTS",
    Webapp: "ACCESS_WEBAPP",
    Configuration: "ACCESS_CONFIGURATION",
    Employees: "ACCESS_EMPLOYEES",
    RolesAndAccess: "ACCESS_ROLES_AND_ACCESS",
    MasterList: "ACCESS_MASTER_LIST",
    NumericalInput: "ACCESS_NUMERICAL_SALES_INPUT",
    VirtualTill: "ACCESS_VIRTUAL_TILL",
    Waitlist: "ACCESS_WAITLIST",
    CardTipsPaidInCash: "ACTIONS_CARD_TIPS_PAID_IN_CASH",
    CashGratuityPaidInCash: "ACTIONS_CASH_GRATUITY_PAID_IN_CASH",
    CardGratuityPaidInCash: "ACTIONS_CARD_GRATUITY_PAID_IN_CASH",
    Pricings: "ACTIONS_PRICINGS",
    Discounts: "ACTIONS_DISCOUNTS",
    Premiums: "ACTIONS_PREMIUMS",
    CustomPricing: "ACTIONS_CUSTOM_PRICING",
    Gratuity: "ACTIONS_GRATUITY",
    CustomGratuity: "ACTIONS_CUSTOM_GRATUITY",
    Taxes: "ACTIONS_TAXES",
    TaxesAccess: "ACCESS_TAXES",
    Refunds: "ACTIONS_REFUNDS",
    Voids: "ACTIONS_VOIDS",
    Cash: "ACTIONS_CASH_PAYMENTS",
    ExternalPayments: "ACTIONS_EXTERNAL_PAYMENTS",
    NoSales: "ACTIONS_NO_SALES",
    DisableCashBalanceVisibility: "ACCESS_DISABLE_CASH_BALANCE_VISIBILITY",
    Terminal: "ACTIONS_TERMINAL_ACTIVATION",
    CloseAccount: "ACTIONS_CLOSE_ACCOUNT",
    EmployeeStatus: "ACCESS_EXTENDED_EMPLOYEE_STATUS",
    ChangeAssignee: "ACTIONS_CHANGE_ASSIGNEE",
    ChangeAssignment: "ACTIONS_CHANGE_ASSIGNMENT",
    ChangeCustomer: "ACTIONS_CHANGE_CUSTOMER",
    SkipSignature: "ACTIONS_SKIP_SIGNATURE",
    ReopenClosedOrder: "ACTIONS_REOPEN_CLOSED_ORDER",
    MergeOrders: "ACTIONS_MERGE_ORDERS",
    TransferItems: "ACTIONS_TRANSFER_ITEMS",
    ManageDeliveries: "ACTIONS_FULFILL_DELIVERIES",
    CreateCustomItem: "ACTIONS_CREATE_CUSTOM_ITEM",
    AdjustPriceOfFixedPriceItems: "ACTIONS_ADJUST_PRICE_OF_FIXED_PRICE_ITEMS",
    DeleteItemsFromPrintedChecks: "ACTIONS_DELETE_ITEMS_FROM_PRINTED_CHECKS",
    ManageShiftReviews: "ACTIONS_MANAGE_SHIFT_REVIEWS",
    OrdersAssignedToOthers: "ACCESS_ORDERS_ASSIGNED_TO_OTHERS",
    OrdersBeforeToday: "ACCESS_ORDERS_BEFORE_TODAY",
    AuditLog: "ACCESS_AUDIT_LOG",
}

export const getDataForRolesTable = (state: StateObject): RoleTableDataItem[] => {
    const accesses = getAccesses(state)
    const res = accesses
        .map(a => {
            switch (a.name) {
                case "Administrator":
                    return { ...a, sortIndex: 0 }

                case "Management":
                    return { ...a, sortIndex: 1 }

                case "Level I":
                    return { ...a, sortIndex: 2 }

                case "Level II":
                    return { ...a, sortIndex: 3 }

                case "Level III":
                    return { ...a, sortIndex: 4 }

                case "Level IV":
                    return { ...a, sortIndex: 5 }

                default:
                    return { ...a, sortIndex: null }
            }
        })
        .map(a => {
            return {
                sortIndex: a.sortIndex,
                accessLevelId: a.id,
                roleName: a.name,
                clockInOut: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.ClockInOut),
                breaks: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.BreaksEnabled),
                limitToDsrAccess: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.LimitToDsrAccess
                ),
                requireClockInToSignIn: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.RequireClockInToSignIn
                ),
                disableCashBalanceVisibility: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.DisableCashBalanceVisibility
                ),
                register: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Register),
                waitlist: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Waitlist),
                operations: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Operations),
                inventory: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Inventory),
                settings: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Settings),
                reports: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Reports),
                webapp: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Webapp),
                auditLog: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.AuditLog),
                configuration: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Configuration),
                employees: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Employees),
                rolesAndAccess: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.RolesAndAccess),
                masterList: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.MasterList),
                discounts: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Discounts),
                premiums: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Premiums),
                customPricing: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.CustomPricing),
                gratuity: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Gratuity),
                customGratuity: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.CustomGratuity),
                taxes: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Taxes),
                taxesAccess: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.TaxesAccess),
                refunds: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Refunds),
                voids: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Voids),
                cash: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Cash),
                externalPayments: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.ExternalPayments
                ),
                noSales: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.NoSales),
                terminal: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.Terminal),
                closeAccount: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.CloseAccount),
                employeeStatus: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.EmployeeStatus),
                numericalInput: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.NumericalInput),
                changeAssignee: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.ChangeAssignee),
                changeAssignment: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.ChangeAssignment
                ),
                changeCustomer: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.ChangeCustomer),
                skipSignature: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.SkipSignature),
                reopenClosedOrder: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.ReopenClosedOrder
                ),
                mergeOrders: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.MergeOrders),
                transferItems: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.TransferItems),
                manageDeliveries: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.ManageDeliveries
                ),
                createCustomItem: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.CreateCustomItem
                ),
                adjustPriceOfFixedPriceItems: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.AdjustPriceOfFixedPriceItems
                ),
                manageShiftReviews: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.ManageShiftReviews
                ),
                virtualTill: !!a.permissions.find((p: PermissionData) => p.name === PermissionType.VirtualTill),
                cardTipsPaidInCash: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.CardTipsPaidInCash
                ),
                cashGratuityPaidInCash: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.CashGratuityPaidInCash
                ),
                cardGratuityPaidInCash: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.CardGratuityPaidInCash
                ),
                deleteItemsFromPrintedChecks: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.DeleteItemsFromPrintedChecks
                ),
                ordersAssignedToOthers: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.OrdersAssignedToOthers
                ),
                ordersBeforeToday: !!a.permissions.find(
                    (p: PermissionData) => p.name === PermissionType.OrdersBeforeToday
                ),
            }
        })
    return sortBy(res, "sortIndex")
}

const findPermissionValue = (
    permissions: PermissionData[],
    key: string
): {
    id: Maybe<string>
    value: boolean
} => {
    const find = permissions.find((p: PermissionData) => p.name === key)
    return find
        ? {
              id: find.id,
              value: true,
          }
        : {
              id: null,
              value: false,
          }
}

export const isRolesAgendaOpened = (state: StateObject) => {
    const query = getCurrentPathQuery(state)
    return query && query.tab && query.tab === "roles"
}

export const getRoleFormData = (state: StateObject): Maybe<RoleFormItem> => {
    const access = getEditRole(state)

    if (!access) {
        return null
    }

    return {
        accessLevelId: access.id,
        roleName: access.name,
        ClockInOut: findPermissionValue(access.permissions, PermissionType.ClockInOut),
        RequireClockInToSignIn: findPermissionValue(access.permissions, PermissionType.RequireClockInToSignIn),
        BreaksEnabled: findPermissionValue(access.permissions, PermissionType.BreaksEnabled),
        UnpaidBreaksEnabled: findPermissionValue(access.permissions, PermissionType.UnpaidBreaksEnabled),
        PaidBreaksEnabled: findPermissionValue(access.permissions, PermissionType.PaidBreaksEnabled),
        LimitToDsrAccess: findPermissionValue(access.permissions, PermissionType.LimitToDsrAccess),
        DisableCashBalanceVisibility: findPermissionValue(
            access.permissions,
            PermissionType.DisableCashBalanceVisibility
        ),
        Register: findPermissionValue(access.permissions, PermissionType.Register),
        Operations: findPermissionValue(access.permissions, PermissionType.Operations),
        OperationsNoSale: findPermissionValue(access.permissions, PermissionType.OperationsNoSale),
        OperationsTipAdjustment: findPermissionValue(access.permissions, PermissionType.OperationsTipAdjustment),
        OperationsDeliveryManagement: findPermissionValue(
            access.permissions,
            PermissionType.OperationsDeliveryManagement
        ),
        OperationsPendingPayments: findPermissionValue(access.permissions, PermissionType.OperationsPendingPayments),
        OperationsShiftReview: findPermissionValue(access.permissions, PermissionType.OperationsShiftReview),
        OperationsInventory: findPermissionValue(access.permissions, PermissionType.OperationsInventory),
        Inventory: findPermissionValue(access.permissions, PermissionType.Inventory),
        Settings: findPermissionValue(access.permissions, PermissionType.Settings),
        Reports: findPermissionValue(access.permissions, PermissionType.Reports),
        Webapp: findPermissionValue(access.permissions, PermissionType.Webapp),
        AuditLog: findPermissionValue(access.permissions, PermissionType.AuditLog),
        Configuration: findPermissionValue(access.permissions, PermissionType.Configuration),
        Pricings: findPermissionValue(access.permissions, PermissionType.Pricings),
        Waitlist: findPermissionValue(access.permissions, PermissionType.Waitlist),
        Discounts: findPermissionValue(access.permissions, PermissionType.Discounts),
        Premiums: findPermissionValue(access.permissions, PermissionType.Premiums),
        CustomPricing: findPermissionValue(access.permissions, PermissionType.CustomPricing),
        Gratuity: findPermissionValue(access.permissions, PermissionType.Gratuity),
        CustomGratuity: findPermissionValue(access.permissions, PermissionType.CustomGratuity),
        Taxes: findPermissionValue(access.permissions, PermissionType.Taxes),
        TaxesAccess: findPermissionValue(access.permissions, PermissionType.TaxesAccess),
        Refunds: findPermissionValue(access.permissions, PermissionType.Refunds),
        Voids: findPermissionValue(access.permissions, PermissionType.Voids),
        Cash: findPermissionValue(access.permissions, PermissionType.Cash),
        ExternalPayments: findPermissionValue(access.permissions, PermissionType.ExternalPayments),
        NoSales: findPermissionValue(access.permissions, PermissionType.NoSales),
        Terminal: findPermissionValue(access.permissions, PermissionType.Terminal),
        NumericalInput: findPermissionValue(access.permissions, PermissionType.NumericalInput),
        CloseAccount: findPermissionValue(access.permissions, PermissionType.CloseAccount),
        EmployeeStatus: findPermissionValue(access.permissions, PermissionType.EmployeeStatus),
        ChangeAssignee: findPermissionValue(access.permissions, PermissionType.ChangeAssignee),
        ChangeAssignment: findPermissionValue(access.permissions, PermissionType.ChangeAssignment),
        ChangeCustomer: findPermissionValue(access.permissions, PermissionType.ChangeCustomer),
        SkipSignature: findPermissionValue(access.permissions, PermissionType.SkipSignature),
        ReopenClosedOrder: findPermissionValue(access.permissions, PermissionType.ReopenClosedOrder),
        MergeOrders: findPermissionValue(access.permissions, PermissionType.MergeOrders),
        TransferItems: findPermissionValue(access.permissions, PermissionType.TransferItems),
        ManageDeliveries: findPermissionValue(access.permissions, PermissionType.ManageDeliveries),
        Employees: findPermissionValue(access.permissions, PermissionType.Employees),
        RolesAndAccess: findPermissionValue(access.permissions, PermissionType.RolesAndAccess),
        MasterList: findPermissionValue(access.permissions, PermissionType.MasterList),
        CreateCustomItem: findPermissionValue(access.permissions, PermissionType.CreateCustomItem),
        AdjustPriceOfFixedPriceItems: findPermissionValue(
            access.permissions,
            PermissionType.AdjustPriceOfFixedPriceItems
        ),
        ManageShiftReviews: findPermissionValue(access.permissions, PermissionType.ManageShiftReviews),
        VirtualTill: findPermissionValue(access.permissions, PermissionType.VirtualTill),
        CardTipsPaidInCash: findPermissionValue(access.permissions, PermissionType.CardTipsPaidInCash),
        CashGratuityPaidInCash: findPermissionValue(access.permissions, PermissionType.CashGratuityPaidInCash),
        CardGratuityPaidInCash: findPermissionValue(access.permissions, PermissionType.CardGratuityPaidInCash),
        DeleteItemsFromPrintedChecks: findPermissionValue(
            access.permissions,
            PermissionType.DeleteItemsFromPrintedChecks
        ),
        OrdersAssignedToOthers: findPermissionValue(access.permissions, PermissionType.OrdersAssignedToOthers),
        OrdersBeforeToday: findPermissionValue(access.permissions, PermissionType.OrdersBeforeToday),
    }
}
